// src/App.js

import React, { Suspense, lazy, useState, useEffect, useCallback, memo } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeProvider } from './ThemeContext';
import SEO from './SEO';
import LandingSection from './LandingSection';
import Contact from './Contact';
import StaticBackToTop from './StaticBackToTop';
import Footer from './Footer';
import Navbar from './Navbar';
import { QrCode } from 'lucide-react';

const GeneratorSection    = lazy(() => import('./GeneratorSection'));
const ScannerSection      = lazy(() => import('./ScannerSection'));
const QrUtilities         = lazy(() => import('./QrUtilities'));
const About               = lazy(() => import('./About'));
const PrivacyPolicy       = lazy(() => import('./PrivacyPolicy'));
const GenerateQrCodePage  = lazy(() => import('./pages/GenerateQrCodePage'));
const ScanQrCodePage      = lazy(() => import('./pages/ScanQrCodePage'));
const QrCodeWithLogoPage  = lazy(() => import('./pages/QrCodeWithLogoPage'));
const WifiQrCodePage      = lazy(() => import('./pages/WifiQrCodePage'));

const prefetchAll = () => {
  import('./GeneratorSection');
  import('./ScannerSection');
  import('./QrUtilities');
};

const pageAnim = {
  initial:    { opacity: 0, y: 12 },
  animate:    { opacity: 1, y: 0  },
  exit:       { opacity: 0, y: -8 },
  transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] },
};

const LoadingFallback = memo(() => (
  <div role="status" aria-live="polite"
    className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0f0f1a]">
    <div className="text-center space-y-5">
      <div className="relative w-20 h-20 mx-auto">
        <div className="absolute inset-0 rounded-full border-4 border-purple-500/20 border-t-purple-500 animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <QrCode size={28} className="text-purple-400 animate-pulse" />
        </div>
      </div>
      <p className="text-gray-700 dark:text-gray-200 font-semibold text-lg">Loading Qronetic</p>
      <p className="text-gray-400 dark:text-gray-500 text-sm">Preparing your QR experience...</p>
    </div>
  </div>
));
LoadingFallback.displayName = 'LoadingFallback';

const skipLinkClass = "sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-purple-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-medium";

const AppShell = memo(({ children, isLanding = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleTabChange = useCallback((tab) => {
    const routes = {
      main: '/generate', utilities: '/utilities', history: '/history',
      contact: '/contact', about: '/about', privacy: '/privacy-policy',
    };
    navigate(routes[tab] || '/generate');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  const handleLogoClick = useCallback(() => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  const handleStart = useCallback(() => {
    navigate('/generate');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  const activeTab = {
    '/generate':       'main',
    '/utilities':      'utilities',
    '/history':        'history',
    '/contact':        'contact',
    '/about':          'about',
    '/privacy-policy': 'privacy',
  }[location.pathname] || '';

  return (
    <div className="bg-white dark:bg-[#0f0f1a] transition-colors duration-500">
      <a href="#main-content" className={skipLinkClass}>Skip to main content</a>
      <Navbar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onLogoClick={handleLogoClick}
        onStart={handleStart}
        isLanding={isLanding}
      />
      <main id="main-content">{children}</main>
      {!isLanding && <StaticBackToTop />}
      <Footer
        onAbout={() => handleTabChange('about')}
        onPrivacy={() => handleTabChange('privacy')}
      />
    </div>
  );
});
AppShell.displayName = 'AppShell';

const Page = memo(({ children }) => (
  <motion.div {...pageAnim} className="hw-accel">{children}</motion.div>
));
Page.displayName = 'Page';

const LandingPage = memo(() => {
  const navigate = useNavigate();
  const handleStart = useCallback(() => {
    navigate('/generate');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);
  return (
    <AppShell isLanding={true}>
      <SEO
        title="Free QR Code Generator & Scanner Online — Create, Customize & Scan | Qronetic"
        description="Qronetic is the #1 free QR code generator and scanner. Create custom QR codes for URLs, Wi-Fi, vCards, text, email & more. Add colors, logos, and download in PNG/SVG. Scan any QR code instantly — no app needed."
        keywords="free qr code generator, qr code scanner online, create qr code free, custom qr code with logo, qr code maker, qronetic"
        path="/"
      />
      <Page><LandingSection onStart={handleStart} /></Page>
    </AppShell>
  );
});
LandingPage.displayName = 'LandingPage';

const GeneratePage = memo(() => (
  <AppShell>
    <SEO
      title="Generate & Scan QR Codes — Free QR Code Tool | Qronetic"
      description="Create custom QR codes and scan existing ones. Supports URLs, text, email, phone, Wi-Fi, vCard. Customize colors, add logos, download PNG/SVG."
      keywords="generate qr code, scan qr code, qr code tool, create qr code online free, qronetic generator"
      path="/generate"
    />
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <GeneratorSection />
        <ScannerSection />
      </Suspense>
    </Page>
  </AppShell>
));
GeneratePage.displayName = 'GeneratePage';

const UtilitiesPage = memo(() => (
  <AppShell>
    <SEO
      title="QR Code Utilities — Batch Tools & Advanced Features | Qronetic"
      description="Access advanced QR code utilities including batch generation, styling options, and more. All free, no sign-up required."
      keywords="qr code utilities, qr code batch generator, qr code tools, advanced qr features"
      path="/utilities"
    />
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <QrUtilities />
      </Suspense>
    </Page>
  </AppShell>
));
UtilitiesPage.displayName = 'UtilitiesPage';

const HistoryPage = memo(() => (
  <AppShell>
    <SEO
      title="QR Code History — View Your Generated QR Codes | Qronetic"
      description="View and manage your previously generated QR codes. Re-download, share, or track your QR code creation history."
      keywords="qr code history, generated qr codes, qr code log"
      path="/history"
      noindex={true}
    />
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <GeneratorSection historyOnly />
      </Suspense>
    </Page>
  </AppShell>
));
HistoryPage.displayName = 'HistoryPage';

const ContactPage = memo(() => (
  <AppShell>
    <SEO
      title="Contact Qronetic — Get in Touch | Qronetic"
      description="Have a question, suggestion, or feedback? Contact the Qronetic team. We'd love to hear from you."
      keywords="contact qronetic, qronetic support, qronetic feedback"
      path="/contact"
    />
    <Page><Contact /></Page>
  </AppShell>
));
ContactPage.displayName = 'ContactPage';

const AboutPage = memo(() => (
  <AppShell>
    <SEO
      title="About Qronetic — Free QR Code Generator & Scanner | Qronetic"
      description="Learn about Qronetic — a free, privacy-first QR code generator and scanner built for everyone. No accounts, no tracking, no limits."
      keywords="about qronetic, qronetic qr code, who made qronetic"
      path="/about"
    />
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <About />
      </Suspense>
    </Page>
  </AppShell>
));
AboutPage.displayName = 'AboutPage';

const PrivacyPage = memo(() => (
  <AppShell>
    <SEO
      title="Privacy Policy — Qronetic"
      description="Read Qronetic's privacy policy. We don't collect, store, or share any personal data. All QR code generation and scanning happens locally in your browser."
      keywords="qronetic privacy policy, qronetic data policy"
      path="/privacy-policy"
    />
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <PrivacyPolicy />
      </Suspense>
    </Page>
  </AppShell>
));
PrivacyPage.displayName = 'PrivacyPage';

// SEO Landing Page wrappers (with AppShell)
const GenerateQrCodeLanding = memo(() => (
  <AppShell>
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <GenerateQrCodePage />
      </Suspense>
    </Page>
  </AppShell>
));
GenerateQrCodeLanding.displayName = 'GenerateQrCodeLanding';

const ScanQrCodeLanding = memo(() => (
  <AppShell>
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <ScanQrCodePage />
      </Suspense>
    </Page>
  </AppShell>
));
ScanQrCodeLanding.displayName = 'ScanQrCodeLanding';

const QrCodeWithLogoLanding = memo(() => (
  <AppShell>
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <QrCodeWithLogoPage />
      </Suspense>
    </Page>
  </AppShell>
));
QrCodeWithLogoLanding.displayName = 'QrCodeWithLogoLanding';

const WifiQrCodeLanding = memo(() => (
  <AppShell>
    <Page>
      <Suspense fallback={<LoadingFallback />}>
        <WifiQrCodePage />
      </Suspense>
    </Page>
  </AppShell>
));
WifiQrCodeLanding.displayName = 'WifiQrCodeLanding';

const NotFoundPage = memo(() => {
  const navigate = useNavigate();
  return (
    <AppShell>
      <SEO
        title="Page Not Found — Qronetic"
        description="The page you're looking for doesn't exist. Go back to Qronetic to generate or scan QR codes for free."
        path="/404"
        noindex={true}
      />
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0f0f1a] px-6">
        <div className="text-center space-y-6 max-w-md">
          <div className="text-8xl font-extrabold text-purple-500/20 dark:text-purple-500/10 select-none">404</div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Page not found</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
              The page you're looking for doesn't exist or has been moved.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm transition-colors duration-200"
          >
            ← Back to Qronetic
          </button>
        </div>
      </div>
    </AppShell>
  );
});
NotFoundPage.displayName = 'NotFoundPage';

const AnimatedRoutes = () => {
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(prefetchAll, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/"                      element={<LandingPage          />} />
        <Route path="/generate"              element={<GeneratePage         />} />
        <Route path="/utilities"             element={<UtilitiesPage        />} />
        <Route path="/history"               element={<HistoryPage          />} />
        <Route path="/contact"               element={<ContactPage          />} />
        <Route path="/about"                 element={<AboutPage            />} />
        <Route path="/privacy-policy"        element={<PrivacyPage          />} />
        {/* SEO Landing Pages */}
        <Route path="/generate-qr-code"      element={<GenerateQrCodeLanding  />} />
        <Route path="/scan-qr-code-online"   element={<ScanQrCodeLanding      />} />
        <Route path="/qr-code-with-logo"     element={<QrCodeWithLogoLanding  />} />
        <Route path="/wifi-qr-code-generator" element={<WifiQrCodeLanding     />} />
        <Route path="*"                      element={<NotFoundPage         />} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => (
  <BrowserRouter>
    <ThemeProvider>
      <AnimatedRoutes />
    </ThemeProvider>
  </BrowserRouter>
);

export default App;